import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ec_admin_token";

const AuthContext = createContext({
    token: null,
    admin: null,
    loading: true,
    login: async () => {},
    logout: () => {},
});

// Axios interceptor — every request automatically carries the admin token.
axios.interceptors.request.use((config) => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

// 401 globally bounces the admin back to the login screen.
axios.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            const wasLoggedIn = !!localStorage.getItem(TOKEN_KEY);
            localStorage.removeItem(TOKEN_KEY);
            if (
                wasLoggedIn &&
                typeof window !== "undefined" &&
                window.location.pathname.startsWith("/admin") &&
                window.location.pathname !== "/admin/login"
            ) {
                window.location.replace("/admin/login");
            }
        }
        return Promise.reject(err);
    }
);

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        async function loadMe() {
            if (!token) {
                setAdmin(null);
                setLoading(false);
                return;
            }
            try {
                const { data } = await axios.get(`${API}/auth/me`);
                if (!cancelled) setAdmin(data);
            } catch (_) {
                if (!cancelled) {
                    localStorage.removeItem(TOKEN_KEY);
                    setToken(null);
                    setAdmin(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        loadMe();
        return () => {
            cancelled = true;
        };
    }, [token]);

    async function login(email, password) {
        const { data } = await axios.post(`${API}/auth/login`, {
            email,
            password,
        });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setToken(data.access_token);
        setAdmin(data.admin);
        return data.admin;
    }

    function logout() {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setAdmin(null);
    }

    return (
        <AuthContext.Provider value={{ token, admin, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
